import { ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { Button, Icon, Label, Popup } from 'semantic-ui-react';
import {
  OtherLrwSessionRoomOverviewDto,
  LrwSessionRoomOverviewDto,
  SpeakingSessionRoomOverviewDto,
  SpeakingSlotStatus,
  SpeakingSlotType,
} from 'api/planning';
import { ScrollClasses, useScrollClick, useScrollDrag } from 'hooks';

type Props = {
  speakingSessionRooms: SpeakingSessionRoomOverviewDto[];
  lrwSessionRooms: LrwSessionRoomOverviewDto[];
  otherLrwSessionRooms: OtherLrwSessionRoomOverviewDto[];
};

type OverviewRow =
  | {
      component: 'LRW';
      roomName: string;
      lrwRoom: LrwSessionRoomOverviewDto;
    }
  | {
      component: 'Speaking';
      roomName: string;
      speakingRoom: SpeakingSessionRoomOverviewDto;
    }
  | {
      component: 'Other LRW';
      roomName: string;
      otherLrwRoom: OtherLrwSessionRoomOverviewDto;
    };

const TIMELINE_ID = 'exam-overview-timeline-slider';
const TIMELINE_START_HOUR = 0;
const TIMELINE_END_HOUR = 24;
const SLOT_MINUTES = 20;
const SLOT_WIDTH = 34;
const TOTAL_HOURS = TIMELINE_END_HOUR - TIMELINE_START_HOUR;
const TOTAL_SLOTS = (TOTAL_HOURS * 60) / SLOT_MINUTES;
const TIMELINE_WIDTH = TOTAL_SLOTS * SLOT_WIDTH;

export const ExamOverviewGrid = ({
  speakingSessionRooms,
  lrwSessionRooms,
  otherLrwSessionRooms,
}: Props) => {
  const { ref: headerRef } = useScrollDrag<HTMLDivElement>(TIMELINE_ID);
  const { onClickLeft, onClickRight } = useScrollClick(24, 8, TIMELINE_ID);

  const rowRefs = useRef<HTMLDivElement[]>([]);
  const cleanupRefs = useRef<Array<(() => void) | undefined>>([]);
  const syncingRef = useRef(false);

  const rows = useMemo<OverviewRow[]>(
    () => [
      ...(lrwSessionRooms ?? []).map((room) => ({
        component: 'LRW' as const,
        roomName: room.name,
        lrwRoom: room,
      })),
      ...(speakingSessionRooms ?? []).map((room) => ({
        component: 'Speaking' as const,
        roomName: room.name,
        speakingRoom: room,
      })),
      ...(otherLrwSessionRooms ?? []).map((room) => ({
        component: 'Other LRW' as const,
        roomName: room.name,
        otherLrwRoom: room,
      })),
    ],
    [lrwSessionRooms, speakingSessionRooms, otherLrwSessionRooms]
  );

  const syncScroll = useCallback(
    (source: HTMLDivElement) => {
      if (syncingRef.current) {
        return;
      }

      syncingRef.current = true;
      const nextScrollLeft = source.scrollLeft;

      if (headerRef.current && headerRef.current !== source) {
        headerRef.current.scrollLeft = nextScrollLeft;
      }

      rowRefs.current.forEach((row) => {
        if (row && row !== source) {
          row.scrollLeft = nextScrollLeft;
        }
      });

      requestAnimationFrame(() => {
        syncingRef.current = false;
      });
    },
    [headerRef]
  );

  useEffect(() => {
    const headerElement = headerRef.current;

    if (!headerElement) {
      return;
    }

    const onHeaderScroll = () => syncScroll(headerElement);

    headerElement.addEventListener('scroll', onHeaderScroll);

    return () => {
      headerElement.removeEventListener('scroll', onHeaderScroll);
    };
  }, [headerRef, syncScroll]);

  useEffect(() => {
    return () => {
      cleanupRefs.current.forEach((cleanup) => cleanup?.());
    };
  }, []);

  const registerRowRef = useCallback(
    (index: number) => (element: HTMLDivElement | null) => {
      cleanupRefs.current[index]?.();
      cleanupRefs.current[index] = undefined;

      if (!element) {
        rowRefs.current[index] = undefined as never;
        return;
      }

      rowRefs.current[index] = element;
      element.scrollLeft = headerRef.current?.scrollLeft ?? 0;

      const onRowScroll = () => syncScroll(element);

      element.addEventListener('scroll', onRowScroll);

      cleanupRefs.current[index] = () => {
        element.removeEventListener('scroll', onRowScroll);
      };
    },
    [headerRef, syncScroll]
  );

  return (
    <S.Grid>
      <S.Subgrid>
        <S.HeaderLeft>
          <S.HeadCell>Component</S.HeadCell>

          <S.RoomHeadCell>
            <S.HeadCellContent>Room</S.HeadCellContent>
            <S.TimelineButton
              type="button"
              basic
              compact
              icon
              aria-label="Scroll timeline left"
              onClick={onClickLeft}
            >
              <Icon name="chevron left" />
            </S.TimelineButton>
          </S.RoomHeadCell>
        </S.HeaderLeft>

        <S.HeaderCenter ref={headerRef} id={TIMELINE_ID}>
          <TimelineHeader />
        </S.HeaderCenter>

        <S.HeaderRight>
          <S.TimelineButton
            type="button"
            basic
            compact
            icon
            aria-label="Scroll timeline right"
            onClick={onClickRight}
          >
            <Icon name="chevron right" />
          </S.TimelineButton>
        </S.HeaderRight>
      </S.Subgrid>

      {rows.map((row, index) => (
        <OverviewRowItem
          key={`${row.component}-${row.roomName}`}
          row={row}
          rowRef={registerRowRef(index)}
        />
      ))}
    </S.Grid>
  );
};

const TimelineHeader = () => {
  const hours = Array.from({ length: TOTAL_HOURS }, (_, index) => TIMELINE_START_HOUR + index);

  return (
    <S.TimelineHeaderGrid>
      {hours.map((hour) => (
        <S.HourCell key={hour}>{String(hour).padStart(2, '0')}:00</S.HourCell>
      ))}
    </S.TimelineHeaderGrid>
  );
};

type OverviewRowItemProps = {
  row: OverviewRow;
  rowRef: (element: HTMLDivElement | null) => void;
};

const OverviewRowItem = ({ row, rowRef }: OverviewRowItemProps) => {
  let preview: ReactElement;

  if (row.component === 'LRW') {
    preview = <LrwPreview room={row.lrwRoom} />;
  } else if (row.component === 'Speaking') {
    preview = <SpeakingPreview room={row.speakingRoom} />;
  } else {
    preview = <OtherLrwPreview room={row.otherLrwRoom} />;
  }

  return (
    <S.Row>
      <S.ComponentCell>
        <ComponentLabel component={row.component} />
      </S.ComponentCell>

      <S.RoomCell>{row.roomName}</S.RoomCell>

      <S.RowCenter ref={rowRef}>
        <S.RowTimelineGrid>
          <TimelineBackground />
          {preview}
        </S.RowTimelineGrid>
      </S.RowCenter>

      <S.RowRight />
    </S.Row>
  );
};

const ComponentLabel = ({ component }: { component: OverviewRow['component'] }) => {
  if (component === 'LRW') {
    return <Label size="mini">LRW</Label>;
  }

  if (component === 'Speaking') {
    return (
      <Label size="mini" color="blue">
        Speaking
      </Label>
    );
  }

  return (
    <Label size="mini" basic>
      Other LRW
    </Label>
  );
};

const TimelineBackground = () => {
  return (
    <S.BackgroundGrid>
      {Array.from({ length: TOTAL_SLOTS }).map((_, index) => {
        const { startMinute, endMinute, slotKey } = getMinuteLabelsForSlot(index);

        return (
          <S.BackgroundCell key={slotKey}>
            <S.MinutesMuted>
              <span>{startMinute}</span>
              <S.MinutesSeparator />
              <span>{endMinute}</span>
            </S.MinutesMuted>
          </S.BackgroundCell>
        );
      })}
    </S.BackgroundGrid>
  );
};

const LrwPreview = ({ room }: { room: LrwSessionRoomOverviewDto }) => {
  const start = getGridColumnStart(room.startTime);
  const span = getGridColumnSpan(room.startTime, room.endTime);
  const occupiedSeats = room.plannedCapacity - room.availableCapacity;
  const isFull = occupiedSeats >= room.plannedCapacity;

  return (
    <S.LrwBar
      $background={isFull ? '#db2828' : '#21ba45'}
      style={{
        gridColumn: `${start} / span ${span}`,
      }}
    >
      <S.BarText>
        {occupiedSeats} / {room.plannedCapacity}
      </S.BarText>
    </S.LrwBar>
  );
};

const OtherLrwPreview = ({ room }: { room: OtherLrwSessionRoomOverviewDto }) => {
  const start = getGridColumnStart(room.startTime);
  const span = getGridColumnSpan(room.startTime, room.endTime);

  return (
    <S.OtherLrwBar
      style={{
        gridColumn: `${start} / span ${span}`,
      }}
    />
  );
};

const SpeakingPreview = ({ room }: { room: SpeakingSessionRoomOverviewDto }) => {
  return (
    <>
      {(room.speakingSlots ?? []).map((slot) => {
        const start = getGridColumnStart(slot.startTime);
        const span = getGridColumnSpan(slot.startTime, slot.endTime);
        const startMinute = getMinuteLabel(slot.startTime);
        const endMinute = getMinuteLabel(slot.endTime);

        const key = `${room.name}-${slot.startTime}-${slot.endTime}-${slot.status}`;

        const slotBar = (
          <S.SpeakingSlot
            key={key}
            $background={getSpeakingSlotColor(slot.status)}
            style={{
              gridColumn: `${start} / span ${span}`,
            }}
          >
            <S.MinutesStrong>
              <span>{startMinute}</span>
              <S.MinutesSeparatorStrong />
              <span>{endMinute}</span>
            </S.MinutesStrong>
          </S.SpeakingSlot>
        );

        const occupiedSeats = slot.plannedCapacity - slot.availableCapacity;
        const isSpeakingWithAvatar = slot.speakingSlotType === SpeakingSlotType.SWA;

        if (isSpeakingWithAvatar && occupiedSeats > 0) {
          return (
            <Popup
              key={key}
              position="top center"
              content={
                <S.SeatCountBadge $background={getSpeakingSlotColor(slot.status)}>
                  {occupiedSeats} / {slot.plannedCapacity}
                </S.SeatCountBadge>
              }
              trigger={slotBar}
            />
          );
        }

        return slotBar;
      })}
    </>
  );
};

const MINUTES_IN_DAY = 24 * 60;

const toMinutes = (time: string) => {
  const normalized = time.slice(0, 5);
  const [hours, minutes] = normalized.split(':').map(Number);

  return hours * 60 + minutes;
};

const getMinuteLabel = (time: string) => time.slice(3, 5);

const getMinuteLabelsForSlot = (slotIndex: number) => {
  const startMinutesFromDayStart = slotIndex * SLOT_MINUTES;
  const startMinute = String(startMinutesFromDayStart % 60).padStart(2, '0');
  const endMinute = String((startMinutesFromDayStart + SLOT_MINUTES) % 60).padStart(2, '0');
  const slotKey = `bg-${startMinutesFromDayStart}-${startMinutesFromDayStart + SLOT_MINUTES}`;

  return {
    startMinute,
    endMinute,
    slotKey,
  };
};

const normalizeRangeForTimeline = (startTime: string, endTime: string) => {
  const timelineStartMinutes = TIMELINE_START_HOUR * 60;
  const timelineEndMinutes = TIMELINE_END_HOUR * 60;

  const startMinutes = toMinutes(startTime);
  const rawEndMinutes = toMinutes(endTime);

  const endMinutes = rawEndMinutes <= startMinutes ? MINUTES_IN_DAY : rawEndMinutes;

  const clampedStartMinutes = Math.max(
    timelineStartMinutes,
    Math.min(startMinutes, timelineEndMinutes)
  );
  const clampedEndMinutes = Math.max(clampedStartMinutes, Math.min(endMinutes, timelineEndMinutes));

  return {
    startMinutes: clampedStartMinutes,
    endMinutes: clampedEndMinutes,
  };
};

const getGridColumnStart = (time: string) => {
  const timelineStartMinutes = TIMELINE_START_HOUR * 60;
  const timelineEndMinutes = TIMELINE_END_HOUR * 60;
  const minutes = Math.max(timelineStartMinutes, Math.min(toMinutes(time), timelineEndMinutes));
  const index = Math.floor((minutes - timelineStartMinutes) / SLOT_MINUTES);

  return Math.max(1, index + 1);
};

const getGridColumnSpan = (startTime: string, endTime: string) => {
  const { startMinutes, endMinutes } = normalizeRangeForTimeline(startTime, endTime);
  const diff = endMinutes - startMinutes;

  return Math.max(1, Math.ceil(diff / SLOT_MINUTES));
};

const getSpeakingSlotColor = (status: SpeakingSlotStatus) => {
  switch (status) {
    case SpeakingSlotStatus.Available:
      return '#21ba45';
    case SpeakingSlotStatus.AwaitingApproval:
      return '#f2711c';
    case SpeakingSlotStatus.Booked:
      return '#db2828';
    case SpeakingSlotStatus.Break:
      return '#767676';
    case SpeakingSlotStatus.Rejected:
      return '#db2828';
    default:
      return '#767676';
  }
};

const S = {
  Grid: styled.div`
    display: grid;
    grid-template-columns: 110px 150px 1fr 40px;
    user-select: none;
  `,
  Subgrid: styled.div`
    display: grid;
    grid-template-columns: subgrid;
    grid-template-rows: 40px;
    grid-column: 1 / -1;
    position: sticky;
    top: 59px;
    z-index: 10;
    background: #fff;
    border-bottom: 1px solid rgba(34, 36, 38, 0.15);
  `,
  HeaderLeft: styled.div`
    display: grid;
    align-items: stretch;
    grid-column: 1 / 3;
    grid-template-columns: 110px 150px;
  `,
  HeaderCenter: styled.div`
    overflow-x: auto;
    scrollbar-width: none;
    cursor: grab;
    user-select: none;
    box-shadow:
      inset -10px 0 10px -10px rgba(34, 36, 38, 0.15),
      inset 10px 0 10px -10px rgba(34, 36, 38, 0.15);

    &.${ScrollClasses.scrollDrag} {
      cursor: grabbing;
    }
  `,
  HeaderRight: styled.div`
    display: flex;
    align-items: stretch;
    justify-content: center;
    background: #fff;
    border-left: 1px solid rgba(34, 36, 38, 0.15);
    border-right: 1px solid rgba(34, 36, 38, 0.15);
  `,
  HeadCell: styled.div`
    display: flex;
    align-items: center;
    padding: 0 10px;
    font-size: 12px;
    font-weight: 700;
    background: #fff;
    border-left: 1px solid rgba(34, 36, 38, 0.15);
    border-right: 1px solid rgba(34, 36, 38, 0.15);
  `,
  HeadCellContent: styled.span`
    font-size: 12px;
    font-weight: 700;
  `,
  RoomHeadCell: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    overflow: hidden;
    background: #fff;
    border-left: 1px solid rgba(34, 36, 38, 0.15);
    border-right: 1px solid rgba(34, 36, 38, 0.15);
    padding-left: 10px;
  `,
  TimelineButton: styled(Button)`
    &&& {
      height: 40px;
      width: 40px;
      min-width: 40px;
      margin: 0;
      border-radius: 0;
      box-shadow: none;
    }
  `,
  TimelineHeaderGrid: styled.div`
    display: grid;
    grid-template-columns: repeat(${TOTAL_SLOTS}, ${SLOT_WIDTH}px);
    width: ${TIMELINE_WIDTH}px;
    min-height: 40px;
    background: #fff;
  `,
  HourCell: styled.div`
    grid-column: span 3;
    display: flex;
    align-items: center;
    padding-left: 8px;
    font-size: 12px;
    border-right: 1px solid rgba(34, 36, 38, 0.1);
    background: #fff;
  `,
  Row: styled.div`
    position: relative;
    display: grid;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
    min-height: 56px;
  `,
  ComponentCell: styled.div`
    display: flex;
    align-items: center;
    padding: 8px 10px;
    background: #f9fafb;
    border-left: 1px solid rgba(34, 36, 38, 0.15);
    border-right: 1px solid rgba(34, 36, 38, 0.15);
    border-bottom: 1px solid rgba(34, 36, 38, 0.1);
  `,
  RoomCell: styled.div`
    display: flex;
    align-items: center;
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 700;
    background: #f9fafb;
    border-bottom: 1px solid rgba(34, 36, 38, 0.1);
  `,
  RowCenter: styled.div`
    overflow-x: auto;
    display: grid;
    grid-template-columns: ${TIMELINE_WIDTH}px;
    background: #f9fafb;
    border-bottom: 1px solid rgba(34, 36, 38, 0.1);
    scrollbar-width: none;

    &:after {
      content: '';
      position: absolute;
      inset: 0;
      pointer-events: none;
      box-shadow:
        inset -10px 0 10px -10px rgba(34, 36, 38, 0.15),
        inset 10px 0 10px -10px rgba(34, 36, 38, 0.15);
    }
  `,
  RowRight: styled.div`
    background: #f9fafb;
    border-right: 1px solid rgba(34, 36, 38, 0.15);
    border-bottom: 1px solid rgba(34, 36, 38, 0.1);
  `,
  RowTimelineGrid: styled.div`
    position: relative;
    display: grid;
    grid-template-columns: repeat(${TOTAL_SLOTS}, ${SLOT_WIDTH}px);
    width: ${TIMELINE_WIDTH}px;
    min-height: 56px;
    align-items: stretch;
  `,
  BackgroundGrid: styled.div`
    grid-column: 1 / -1;
    grid-row: 1;
    display: grid;
    grid-template-columns: subgrid;
    min-height: 56px;
  `,
  BackgroundCell: styled.div`
    position: relative;
    background: #f9fafb;
    border-right: 1px solid rgba(34, 36, 38, 0.06);
  `,
  LrwBar: styled.div<{ $background: string }>`
    grid-row: 1;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    background: ${({ $background }) => $background};
    border-right: 1px solid rgba(0, 0, 0, 0.04);
  `,
  OtherLrwBar: styled.div`
    grid-row: 1;
    z-index: 1;
    align-self: stretch;
    background: #d9d9d9;
    border-right: 1px solid rgba(0, 0, 0, 0.04);
  `,
  SpeakingSlot: styled.div<{ $background: string }>`
    position: relative;
    grid-row: 1;
    z-index: 1;
    align-self: stretch;
    background: ${({ $background }) => $background};
    border-right: 1px solid rgba(0, 0, 0, 0.04);
  `,
  BarText: styled.span`
    font-size: 11px;
    font-weight: 700;
    color: #fff;
  `,
  SeatCountBadge: styled.span<{ $background: string }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 10px;
    border-radius: 12px;
    background: ${({ $background }) => $background};
    color: #fff;
    font-size: 12px;
    font-weight: 700;
  `,
  MinutesMuted: styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    font-size: 9px;
    line-height: 9px;
    color: rgba(0, 0, 0, 0.22);
    pointer-events: none;
  `,
  MinutesStrong: styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    font-size: 9px;
    line-height: 9px;
    color: rgba(255, 255, 255, 0.92);
    pointer-events: none;
  `,
  MinutesSeparator: styled.div`
    width: 6px;
    height: 1px;
    background: rgba(0, 0, 0, 0.22);
  `,
  MinutesSeparatorStrong: styled.div`
    width: 6px;
    height: 1px;
    background: rgba(255, 255, 255, 0.92);
  `,
};
